import React from "react";
import { Link } from "react-router-dom";
import { ExternalLink, BookOpen } from "lucide-react";

/**
 * /fuentes — página pública de atribución y fuentes de datos.
 *
 * Motivación: el catastro provincial (IDECOR) se publica bajo CC BY-SA 4.0,
 * que exige atribución al reutilizar los datos. En vez de contaminar cada
 * pantalla con menciones técnicas, centralizamos la atribución acá y linkeamos
 * discretamente desde el footer y desde las fichas de lote.
 */
export default function Fuentes() {
  return (
    <div className="min-h-screen bg-slate-100" data-testid="fuentes-page">
      <header className="border-b border-slate-300 bg-white px-6 py-6">
        <div className="max-w-3xl mx-auto flex items-center gap-3">
          <BookOpen className="w-6 h-6 text-emerald-700" />
          <div>
            <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-500">LandHo!</p>
            <h1 className="font-heading text-2xl font-bold text-slate-900">Fuentes de datos y créditos</h1>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-10 space-y-10">
        <section>
          <p className="text-sm text-slate-700 leading-relaxed">
            Radar cruza datos catastrales oficiales, normativa urbanística
            municipal y publicaciones de portales inmobiliarios. Todas las
            fuentes públicas usadas se atribuyen aquí y respetan las licencias
            correspondientes.
          </p>
        </section>

        <Fuente
          titulo="Catastro Provincial de Córdoba"
          via="IDECOR — Infraestructura de Datos Espaciales de Córdoba"
          descripcion="Parcelas catastrales, valuaciones, normativa urbanística municipal y uso del suelo."
          licencia="Creative Commons Atribución-CompartirIgual 4.0 Internacional (CC BY-SA 4.0)"
          licenciaUrl="https://creativecommons.org/licenses/by-sa/4.0/deed.es"
          linkTexto="mapascordoba.gob.ar"
          linkUrl="https://www.mapascordoba.gob.ar/"
          nota="El uso de estos datos se acompaña de esta atribución y se comparte bajo la misma licencia. Las capas base utilizadas incluyen la normativa provincial consolidada, ordenanzas municipales publicadas por IDECOR y el uso del suelo 2022."
        />

        <Fuente
          titulo="Imágenes satelitales"
          via="Esri World Imagery"
          descripcion="Tiles satelitales de alta resolución usados en el mapa modo 'Satélite'."
          licencia="Términos de servicio de Esri (uso permitido con atribución)."
          licenciaUrl="https://www.esri.com/en-us/legal/terms/full-master-agreement"
          linkTexto="Esri, Maxar, Earthstar Geographics"
          linkUrl="https://www.esri.com/en-us/arcgis/products/arcgis-world-imagery"
        />

        <Fuente
          titulo="Callejero y geocodificación"
          via="OpenStreetMap · Nominatim · CartoDB"
          descripcion="Callejero base, geocodificación inversa (dirección desde coordenadas) y polígonos administrativos de OpenStreetMap."
          licencia="Open Database License (ODbL)"
          licenciaUrl="https://www.openstreetmap.org/copyright"
          linkTexto="openstreetmap.org"
          linkUrl="https://www.openstreetmap.org/"
          nota="© contribuyentes de OpenStreetMap. Nominatim se usa respetando el rate limit oficial (1 req/s) y con caché local para minimizar carga."
        />

        <Fuente
          titulo="Búsqueda de avisos publicados"
          via="Google Programmable Search"
          descripcion="Búsqueda federada sobre los 7 portales inmobiliarios de Córdoba (Zonaprop, Argenprop, MercadoLibre, Properati, Remax, La Voz, Agrofy)."
          licencia="Términos de servicio de Google."
          licenciaUrl="https://policies.google.com/terms"
          linkTexto="programmablesearchengine.google.com"
          linkUrl="https://programmablesearchengine.google.com/"
        />

        <section className="border-t border-slate-300 pt-8">
          <h2 className="font-heading text-lg font-bold text-slate-900 mb-3">Términos y condiciones</h2>
          <p className="text-sm text-slate-600 leading-relaxed">
            Los datos que muestra Radar son informativos, se generan a partir de
            fuentes públicas y su exactitud depende de la fecha de actualización
            de cada organismo. Radar no reemplaza la verificación catastral,
            legal ni topográfica que corresponde hacer sobre cada lote antes de
            operar. Las estimaciones de m² construibles y unidades máximas son
            un cálculo automático a partir de la ordenanza vigente; se recomienda
            siempre consultar el texto oficial de la norma y al municipio
            correspondiente.
          </p>
          <p className="text-xs text-slate-500 mt-4 italic">
            (Documento de términos completos: pendiente de publicación.)
          </p>
        </section>

        <section className="border-t border-slate-300 pt-8 text-center">
          <Link
            to="/radar/tierra"
            className="inline-flex items-center gap-2 text-sm font-bold uppercase tracking-wider text-emerald-700 hover:text-emerald-900"
            data-testid="fuentes-volver"
          >
            ← Volver al radar
          </Link>
        </section>
      </main>
    </div>
  );
}

function Fuente({ titulo, via, descripcion, licencia, licenciaUrl, linkTexto, linkUrl, nota }) {
  return (
    <section className="border-l-2 border-emerald-700 pl-5" data-testid={`fuente-${titulo.toLowerCase().replace(/\s+/g, "-")}`}>
      <h2 className="font-heading text-lg font-bold text-slate-900">{titulo}</h2>
      <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-emerald-800 mt-0.5">{via}</p>
      <p className="text-sm text-slate-700 mt-2 leading-relaxed">{descripcion}</p>
      <dl className="text-xs text-slate-600 mt-3 space-y-1">
        <div>
          <dt className="inline font-semibold">Licencia: </dt>
          <dd className="inline">
            <a href={licenciaUrl} target="_blank" rel="noopener noreferrer"
               className="underline hover:text-slate-900">{licencia}</a>
          </dd>
        </div>
        <div>
          <dt className="inline font-semibold">Sitio: </dt>
          <dd className="inline">
            <a href={linkUrl} target="_blank" rel="noopener noreferrer"
               className="underline hover:text-slate-900 inline-flex items-center gap-1">
              {linkTexto} <ExternalLink className="w-3 h-3" />
            </a>
          </dd>
        </div>
      </dl>
      {nota && (
        <p className="text-[11px] text-slate-500 mt-3 italic leading-relaxed">{nota}</p>
      )}
    </section>
  );
}
